import { CASES } from "./constants";
import { safeGetJSON } from "tap/utils/safeLocalStorage";
import {
  DEFAULT_PAGE_SIZE_OPTIONS,
  getInitialPaginationRange,
  getStoredItemsPerPage,
  PAGINATION_STORAGE_KEYS,
  persistItemsPerPageFromPayload,
  withPreservedPagination,
} from "tap/utils/paginationPreferences.js";

const paginationKeys = {
  itemsPerPage: PAGINATION_STORAGE_KEYS.users,
};
const initialItemsPerPage = getStoredItemsPerPage(paginationKeys.itemsPerPage);
const initialPaginationRange = getInitialPaginationRange(initialItemsPerPage);

/**
 * Retrieves persisted sort preference from localStorage.
 * @returns {Array} Sort tuple [field, direction], defaults to ['id', 'ASC']
 */
const getInitialUserSort = () => {
  return safeGetJSON("useSort", ["id", "ASC"]);
};

/**
 * Users Redux State Shape
 * @typedef {Object} UsersState
 * @property {Array} users - List of user records from API
 * @property {boolean} refreshUsers - Flag to trigger grid data refetch
 * @property {Object} filterForm - Search/filter form values
 * @property {Object} userForm - Create/edit form values
 * @property {Array} roleOptions - Available role options from API
 * @property {Array} statusOptions - User status options (ACTIVE, DISABLED, INACTIVE)
 * @property {string} selectedUser - Currently selected user ID
 * @property {number} totalPages - Pagination total pages
 * @property {number} totalItems - Total record count
 * @property {number} currentPage - Current page index
 * @property {string} filterParam - Serialized filter query string
 * @property {Array} userRange - Pagination range [start, end]
 * @property {Array} userSort - Sort tuple [field, direction]
 * @property {number} itemsPerPage - Records per page
 * @property {string|null} drawerMode - Current drawer state: 'create' | 'edit' | null
 */
const initialState = {
  users: [],
  refreshUsers: false,
  filterForm: {
    name: "",
    email: "",
    role: "",
  },
  userForm: {
    id: "",
    username: "",
    fullName: "",
    ermUserName: "",
    spaUserRoleIds: "",
    status: "",
    rightslineId: "",
  },
  roleOptions: [],
  statusOptions: [
    { value: "ACTIVE", label: "ACTIVE" },
    { value: "DISABLED", label: "DISABLED" },
    { value: "INACTIVE", label: "INACTIVE" },
  ],
  selectedUser: "",
  totalPages: 0,
  totalItems: 0,
  currentPage: 0,
  filterParam: "",
  userRange: initialPaginationRange,
  userSort: getInitialUserSort(),
  defaultFilterRange: initialPaginationRange,
  itemsPerPage: initialItemsPerPage,
  pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
  drawerMode: null,
};

export function users(state = initialState, action) {
  const { type, payload } = action;
  switch (type) {
    case CASES.UPDATE_USERS: {
      // Persist sort preference to localStorage for session continuity
      if (payload.userSort) {
        localStorage.setItem("userSort", JSON.stringify(payload.userSort));
      }
      persistItemsPerPageFromPayload(payload, paginationKeys);
      return { ...state, ...payload };
    }
    case CASES.UPDATE_USERS_SEARCH_FORM: {
      const { data, key } = payload;
      return { ...state, [key]: { ...state[key], ...data } };
    }
    case CASES.UPDATE_USER_FORM: {
      const { data, key } = payload;
      return { ...state, [key]: { ...state[key], ...data } };
    }
    case CASES.CLEAR_USER_FORM: {
      return {
        ...state,
        userForm: { ...initialState.userForm },
        selectedUser: initialState.selectedUser,
      };
    }
    case CASES.CLEAR_USERS: {
      return withPreservedPagination(
        state,
        {
          ...initialState,
          roleOptions: state.roleOptions,
          userSort: state.userSort,
        },
        ["userRange"],
      );
    }
    default:
      return state;
  }
}
